import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { DateField } from '../../../../components/inputs/DateField';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { annualProcurementPlansApi } from '../procurementPlansApi';
import type {
  AnnualProcurementPlanCandidate,
  AnnualProcurementPlanDetail,
} from '../procurementPlanTypes';

/**
 * Create or correct one annual procurement plan.
 *
 * **Five fields**: the budget plan it is framed by, the submission deadline, the coverage
 * period, a description and a note. The APP NUMBER is not among them — it is server-assigned,
 * which is what makes it unspoofable — and neither is the FISCAL YEAR, which is the budget
 * plan's and is read through the join.
 *
 * **The budget plan is chosen on a CREATE and STATED on an edit.** A plan is never re-pointed
 * at another year: its number quotes the year it was raised under, and every PPMP beneath it
 * was filed against that year's PPAs. The server ignores the field on an update, and this form
 * does not draw a control the server would not honour.
 */
export function AnnualProcurementPlanModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: AnnualProcurementPlanDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [budgetPlanId, setBudgetPlanId] = useState(editing?.budgetPlanId ?? '');
  const [submissionClosesAt, setSubmissionClosesAt] = useState(editing?.submissionClosesAt ?? '');
  const [coverageStartDate, setCoverageStartDate] = useState(editing?.coverageStartDate ?? '');
  const [coverageEndDate, setCoverageEndDate] = useState(editing?.coverageEndDate ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [candidates, setCandidates] = useState<AnnualProcurementPlanCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(!editing);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The picker is only on a CREATE, so the feed is too — an edit states the plan it is already
    framed by and has nothing to choose from.
  */
  useEffect(() => {
    if (editing) return;

    const controller = new AbortController();

    annualProcurementPlansApi
      .candidates(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCandidates(rows);
      })
      .catch(() => {
        // Fails soft — the save still refuses an unknown or already-framed plan, so the
        // server stays the authority on what may be framed.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCandidates(false);
      });

    return () => controller.abort();
  }, [editing]);

  const options: SelectOption[] = candidates.map((c) => ({
    value: c.budgetPlanId,
    // The NUMBER is how a reader knows a budget plan, so it is the label rather than the
    // sublabel — the one case where a bare code is what the reader recognises.
    label: c.planNumber,
    sublabel: `FY ${c.fiscalYear}`,
    keywords: String(c.fiscalYear),
  }));

  /**
   * Choosing a budget plan SEEDS the three dates from its own window.
   *
   * A procurement plan ordinarily covers the year its budget does, and asking a reader to
   * retype three dates they have just chosen a plan for is asking for a transcription error.
   * They stay editable — the APP owns its window from here on, and a later edit to the budget
   * plan deliberately does not propagate.
   */
  function chooseBudgetPlan(next: string) {
    setBudgetPlanId(next);

    const chosen = candidates.find((c) => c.budgetPlanId === next);

    if (chosen) {
      setSubmissionClosesAt(chosen.submissionClosesAt);
      setCoverageStartDate(chosen.coverageStartDate);
      setCoverageEndDate(chosen.coverageEndDate);
    }
  }

  const canSave =
    (editing !== null || budgetPlanId.length > 0) &&
    submissionClosesAt.length > 0 &&
    coverageStartDate.length > 0 &&
    coverageEndDate.length > 0 &&
    confirmed &&
    !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      budgetPlanId: editing?.budgetPlanId ?? budgetPlanId,
      submissionClosesAt,
      coverageStartDate,
      coverageEndDate,
      description: description.trim() || null,
      notes: notes.trim() || null,
    };

    try {
      if (editing) await annualProcurementPlansApi.update(editing.id, request);
      else await annualProcurementPlansApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not save the annual procurement plan.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'annual-procurement-plan' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing
          ? `Edit ${editing.appNumber}`
          : 'Create an annual procurement plan'
      }
      size="2xl"
      id="annual-procurement-plan-modal"
      autogenerate={{
        /*
          Fills every required field, so the primary action enables on the first try. The
          budget plan is picked from the LIVE candidate list — an invented id would be refused
          and read as a bug in the form — and choosing it seeds the three dates, which is what
          makes one click produce a submittable form.

          On an EDIT the plan is not a field, so there is nothing to pick: the dates already
          carry real values and the generator fills only the prose.
        */
        onGenerate: () => {
          if (!editing && options.length > 0) {
            chooseBudgetPlan(options[Math.floor(Math.random() * options.length)].value);
          }

          setDescription('Annual procurement plan generated for testing.');
          setNotes('Filed by Autogenerate.');
        },
        disabled: saving || (!editing && options.length === 0),
      }}
      confirm={{
        id: 'annual-procurement-plan-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="annual-procurement-plan-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="annual-procurement-plan-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {editing ? (
          /*
            STATED, not offered — the server ignores the field on an update, and a control the
            server would not honour is a control that lies. A read-only FACT, so it is the
            Table Display View rather than a disabled picker.
          */
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'APP number', value: editing.appNumber, mono: true },
              { label: 'Budget plan', value: editing.budgetPlanNumber, mono: true },
              { label: 'Fiscal year', value: String(editing.fiscalYear), mono: true },
            ]}
          />
        ) : (
          <Select
            id="annual-procurement-plan-budget-plan-id"
            label="Budget plan"
            required
            description="The budget year this procurement plan is framed by. It supplies the fiscal year, and choosing it fills the three dates below from the budget plan's own window."
            value={budgetPlanId || null}
            onChange={chooseBudgetPlan}
            options={options}
            loading={loadingCandidates}
            clearable={false}
            disabled={saving}
            error={error?.fieldError('budgetPlanId')}
          />
        )}

        {/*
          The one thing the picker cannot say: what happens when the list is empty. One plan
          frames one year, so a fully-planned organization has nothing left to choose.
        */}
        {!editing && !loadingCandidates && options.length === 0 ? (
          <p className="text-xs text-slate-500">
            Every budget plan already has an annual procurement plan — one plan frames one year.
            Create a budget plan for another fiscal year, or delete the procurement plan that is
            framing this one.
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="annual-procurement-plan-submission-closes-at"
            label="Submission deadline"
            required
            description="The last day a department may file its PPMP under this plan. Departments routinely file for next year months before it begins, so this is not held to the coverage start."
            value={submissionClosesAt}
            onChange={setSubmissionClosesAt}
            disabled={saving}
            error={error?.fieldError('submissionClosesAt')}
          />

          <DateField
            id="annual-procurement-plan-coverage-start-date"
            label="Coverage from"
            required
            description="The first day of the period this plan covers."
            value={coverageStartDate}
            onChange={setCoverageStartDate}
            disabled={saving}
            error={error?.fieldError('coverageStartDate')}
          />

          <DateField
            id="annual-procurement-plan-coverage-end-date"
            label="Coverage to"
            required
            description="The last day. It may equal the start — a plan covering a single day is odd but not wrong."
            value={coverageEndDate}
            onChange={setCoverageEndDate}
            disabled={saving}
            error={error?.fieldError('coverageEndDate')}
          />

          {/*
            An odd field count, and the last one sits alone on its row rather than being
            stranded in a grid of its own — the three dates flow together and the grid wraps
            them, which is what the layout rule asks for.
          */}
        </div>

        {editing ? (
          <p className="text-xs text-slate-500">
            Correcting the window leaves {editing.appNumber} alone. The number records the year
            the plan was raised under, which is a fact about an instant.
          </p>
        ) : null}

        <Textarea
          id="annual-procurement-plan-description"
          label="Description"
          description="What this year's procurement is for, in a sentence or two."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
          minRows={3}
          error={error?.fieldError('description')}
        />

        {/* A free note about the record as a whole — full width, outside the grid. */}
        <Textarea
          id="annual-procurement-plan-notes"
          label="Notes"
          description="Anything a later reader needs to know about this plan."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={saving}
          minRows={3}
          error={error?.fieldError('notes')}
        />

        {!editing && budgetPlanId ? (
          <p className="text-xs text-slate-500">
            The plan number is assigned on save, as{' '}
            <span className="font-mono">
              APP-{candidates.find((c) => c.budgetPlanId === budgetPlanId)?.fiscalYear ?? 'YYYY'}
              -NNNN
            </span>
            , and never changes.
            {submissionClosesAt
              ? ` Departments may file until ${formatDateOnly(submissionClosesAt)}.`
              : ''}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
